/**
 * Auth client — reads the SWA identity, with a local dev mock.
 *
 * Production: fetch `/.auth/me`, redirect to `/.auth/login/aad` to sign in and
 * `/.auth/logout` to sign out. Entra External ID is configured behind the `aad`
 * provider in staticwebapp.config.json.
 *
 * Local dev (no Azure): `/.auth/me` does not exist, so we fall back to a mock
 * session persisted in localStorage. Sign-in just stamps the mock principal.
 */
import type { SessionUser, SwaAuthMeResponse, SwaClientPrincipal } from './types';

const MOCK_KEY = 'solandia:mock-auth';
// Custom OpenID Connect provider "solandia" → Microsoft Entra External ID (CIAM)
// tenant getsolandia, configured in staticwebapp.config.json.
const LOGIN_PATH = '/.auth/login/solandia';
const LOGOUT_PATH = '/.auth/logout';

/** True when running against the real SWA platform (heuristic: not localhost). */
function looksLikeSwa(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1' && host !== '0.0.0.0';
}

function principalToUser(p: SwaClientPrincipal): SessionUser {
  const nameClaim = p.claims?.find(
    (c) => c.typ === 'name' || c.typ.endsWith('/name'),
  )?.val;
  const emailClaim = p.claims?.find(
    (c) => c.typ === 'emails' || c.typ === 'email' || c.typ.endsWith('/emailaddress'),
  )?.val;
  return {
    id: p.userId,
    displayName: nameClaim ?? p.userDetails ?? 'Používateľ',
    email: emailClaim ?? p.userDetails ?? '',
    provider: p.identityProvider,
  };
}

function readMock(): SessionUser | null {
  try {
    const raw = window.localStorage.getItem(MOCK_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

/** Resolve the current session, or null when signed out. */
export async function getSession(): Promise<SessionUser | null> {
  // Try the real SWA endpoint first; it only exists when hosted on SWA.
  if (looksLikeSwa()) {
    try {
      const res = await fetch('/.auth/me', { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const data = (await res.json()) as SwaAuthMeResponse;
        if (data.clientPrincipal) return principalToUser(data.clientPrincipal);
        return null;
      }
    } catch {
      // fall through to mock (e.g. offline / endpoint unavailable)
    }
  }
  return readMock();
}

/**
 * Begin sign-in. On SWA, redirect to the custom-OIDC ("solandia") login;
 * locally, stamp a mock user.
 *
 * `uiLocale` (the app's active locale) is forwarded as `ui_locales` (and the
 * hyphenated `ui-locales` alias) so Entra External ID renders its hosted
 * sign-in / sign-up pages in the chosen language. NOTE: this is best-effort —
 * SWA's custom-OIDC layer may not forward arbitrary query params to the
 * upstream authorize endpoint; if it doesn't, the param is simply ignored and
 * Entra falls back to its own browser/tenant default. See notes in the PR.
 */
export function signIn(uiLocale?: string): void {
  if (looksLikeSwa()) {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    let url = `${LOGIN_PATH}?post_login_redirect_uri=${redirect}`;
    if (uiLocale) {
      const loc = encodeURIComponent(uiLocale);
      url += `&ui_locales=${loc}&ui-locales=${loc}`;
    }
    window.location.assign(url);
    return;
  }
  const mock: SessionUser = {
    id: 'mock-parent-1',
    displayName: 'Jana Rodičová',
    email: 'jana@solandia.local',
    provider: 'mock',
  };
  window.localStorage.setItem(MOCK_KEY, JSON.stringify(mock));
  window.location.reload();
}

/** Sign out. On SWA, redirect to logout; locally, clear the mock session. */
export function signOut(): void {
  if (looksLikeSwa()) {
    window.location.assign(`${LOGOUT_PATH}?post_logout_redirect_uri=/`);
    return;
  }
  window.localStorage.removeItem(MOCK_KEY);
  window.location.assign('/');
}

/** Is the app running against the real Azure SWA platform? */
export function isHostedOnSwa(): boolean {
  return looksLikeSwa();
}
